import React, { useState, useEffect } from 'react';
import { HandThumbUpIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon } from '@heroicons/react/24/solid';
import intranetApi from '../../services/intranetApi';

const PostLikes = ({ postId, likeCount, userLiked, onToggleLike, className = '' }) => {
  const [likes, setLikes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipLoaded, setTooltipLoaded] = useState(false);

  const fetchLikes = async () => {
    if (tooltipLoaded || loading || likeCount === 0) return;
    
    setLoading(true);
    try {
      const response = await intranetApi.posts.getPostLikes(postId);
      setLikes(response.data.likes || []);
      setTooltipLoaded(true);
    } catch (error) {
      console.error('Error fetching post likes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
    fetchLikes();
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const formatLikesTooltip = () => {
    if (loading) return 'Loading...';
    if (likes.length === 0) return 'No likes yet';
    
    if (likes.length === 1) {
      return `${likes[0].name} liked this`;
    } else if (likes.length === 2) {
      return `${likes[0].name} and ${likes[1].name} liked this`;
    } else if (likes.length <= 5) {
      const names = likes.slice(0, -1).map(like => like.name).join(', ');
      return `${names} and ${likes[likes.length - 1].name} liked this`;
    } else {
      const firstThree = likes.slice(0, 3).map(like => like.name).join(', ');
      const remaining = likes.length - 3;
      return `${firstThree} and ${remaining} others liked this`;
    }
  };

  const getInlineVisual = () => {
    if (likeCount === 0) return null;
    
    // Show first few profile initials for visual indication
    const displayLikes = likes.slice(0, 3);
    
    if (displayLikes.length === 0 && !loading) {
      // If we haven't loaded likes yet, show generic indicators
      return (
        <div className="flex -space-x-1">
          {Array.from({ length: Math.min(likeCount, 3) }).map((_, index) => (
            <div
              key={index}
              className="w-5 h-5 bg-blue-100 border border-white rounded-full flex items-center justify-center"
            >
              <HandThumbUpSolidIcon className="w-2.5 h-2.5 text-blue-600" />
            </div>
          ))}
          {likeCount > 3 && (
            <div className="w-5 h-5 bg-gray-100 border border-white rounded-full flex items-center justify-center">
              <span className="text-xs text-gray-600 font-medium">+{likeCount - 3}</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex -space-x-1">
        {displayLikes.map((like, index) => (
          <div
            key={like.id}
            className="w-5 h-5 bg-blue-100 border border-white rounded-full flex items-center justify-center"
            title={like.name}
          >
            <span className="text-xs text-blue-600 font-medium">
              {like.name.charAt(0).toUpperCase()}
            </span>
          </div>
        ))}
        {likeCount > 3 && (
          <div className="w-5 h-5 bg-gray-100 border border-white rounded-full flex items-center justify-center">
            <span className="text-xs text-gray-600 font-medium">+{likeCount - 3}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <button 
        onClick={onToggleLike}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`flex items-center space-x-2 transition-colors duration-200 ${
          userLiked 
            ? 'text-blue-600 hover:text-blue-700' 
            : 'text-gray-400 hover:text-blue-500'
        }`}
        title={userLiked ? 'Unlike post' : 'Like post'}
      >
        <div className="flex items-center space-x-1">
          {userLiked ? (
            <HandThumbUpSolidIcon className="h-5 w-5" />
          ) : (
            <HandThumbUpIcon className="h-5 w-5" />
          )}
          <span className="text-sm">{likeCount || 0}</span>
        </div>
        
        {/* Inline visual indicators */}
        {likeCount > 0 && (
          <div className="ml-1">
            {getInlineVisual()}
          </div>
        )}
      </button>

      {/* Tooltip */}
      {showTooltip && likeCount > 0 && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-10">
          <div className="bg-gray-900 text-white text-sm rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
            {formatLikesTooltip()}
            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostLikes; 